import { Injectable } from '@angular/core';
import { OrderApiService } from './order.api.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {CreateSharedOrder, SharedOrderAddItems, SharedOrderResponse} from '../models/shared-order.model';
import {dataToSharedOrderResponse} from '../dto/shared-order.dto';
import { map } from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {CartStore} from '../stores/cart.store';
import {PaymentStore} from '../stores/payment.store';
import {UrlParam} from '../enums/group-param.enum';
import {RestaurantStore} from '../stores/restaurant.store';
import {MenuItemData, MenuItemForPayment, MenuItemVariation} from '../models/cart-item.model';
import {Price, ProductModifier} from '../models/restaurant.model';
import { SharedOrdersModalComponent } from '../../pages/restaurant/components/shared-orders-modal/shared-orders-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class SharedOrderService {
  private sharedOrderId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private sharedOwnerId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private sharedOrder: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  constructor(
    private apiService: OrderApiService,
    private route: ActivatedRoute,
    private cartStore: CartStore,
    private paymentStore: PaymentStore,
    private restaurantStore: RestaurantStore,
    private dialog: MatDialog,
  ) { }

  public get singleOrderIds(): string[] {
    const singleOrderIds = [];
    if (this.sharedOrder.value) {
      this.sharedOrder.value.map(order => {
        singleOrderIds.push(order.single_order_id);
      });
      return singleOrderIds;
    }
  }

  public get sharedOrderId$(): string {
    return this.sharedOrderId.value;
  }

  public get sharedOwnerId$(): string {
    return this.sharedOwnerId.value;
  }

  public set sharedOwnerId$(orderId: string) {
    this.sharedOwnerId.next(orderId);
  }

  public sharedOwnerIdSubscribe(): Observable<any> {
    return this.sharedOwnerId.asObservable();
  }

  public createSharedOrder(locationId: string, sharedBody: CreateSharedOrder): Observable<SharedOrderResponse> {
    return this.apiService.post(`api/locations/${locationId}/shared_orders`, sharedBody).pipe(
      map(response => dataToSharedOrderResponse(response))
    );
  }

  public getAllSharedOrders(locationId: string): void {
    this.paymentStore.isWaitingUpdate = true;
    this.apiService.get(`api/shared_orders/${this.sharedOwnerId$}/orders`).subscribe(response => {
     this.calculateSingleOrder(locationId, response);
    }, () => {
      this.paymentStore.isWaitingUpdate = false;
    });
  }

  public addToSharedOrder(locationId: string): Observable<any> {
    const orderMap = this.cartStore.orderMap[locationId];
    const sharedOrder = new SharedOrderAddItems(
      orderMap.order.createOrderRequest,
      this.paymentStore.givenName,
      this.paymentStore.familyName,
      orderMap.id
    );
    return this.apiService.post(`api/shared_orders/${this.sharedOrderId$}/orders`, sharedOrder);
  }

  public getSharedOrderId(): void {
    const getSharedOrderId = sessionStorage.getItem(UrlParam.sharedOrderId);
    if (this.route.snapshot.queryParams.sharedOrderId) {
      sessionStorage.setItem(UrlParam.sharedOrderId, this.route.snapshot.queryParams.sharedOrderId);
      this.sharedOrderId.next(this.route.snapshot.queryParams.sharedOrderId);
    } else if (getSharedOrderId) {
      this.sharedOrderId.next(getSharedOrderId);
    }
  }

  public getSharedOwnerId(): void {
    const getSharedOrderId = sessionStorage.getItem(UrlParam.sharedOwnerId);
    if (this.route.snapshot.queryParams.sharedOwnerId) {
      sessionStorage.setItem(UrlParam.sharedOwnerId, this.route.snapshot.queryParams.sharedOwnerId);
      this.sharedOwnerId.next(this.route.snapshot.queryParams.sharedOwnerId);
    } else if (getSharedOrderId) {
      this.sharedOwnerId.next(getSharedOrderId);
    }
  }

  public openSharedOrderViewModal(): void {
    event.stopPropagation();
    this.dialog.open(SharedOrdersModalComponent, {
      data: {
        restaurant: null,
        isView: true
      },
      height: 'fit-content',
      width: '600px',
    });
  }

  public calculateSingleOrder(locationId: string, sharedOrder: any[] = []): void {
    const orderMap = this.cartStore.orderMap[locationId];
    const order = this.apiService.getOrderWithTipMoney(locationId);
    const lineItems = [];
    const singleOrderIds = [];
    sharedOrder.map(createdOrder => {
      singleOrderIds.push(createdOrder.single_order_id);
      const sharedOrderSaved = this.sharedOrder.value;
      if (sharedOrderSaved && sharedOrderSaved?.length) {
        if (sharedOrderSaved.some(orderDefault => createdOrder.single_order_id === orderDefault.single_order_id)) {
          return;
        }
      }
      createdOrder.order_items.line_items.map(item => {
        lineItems.push(item);
      });
    });
    order.createOrderRequest.line_items.push(...lineItems);
    order.is_shared_order = true;
    order.shared_order_id = this.sharedOwnerId$;
    order.single_order_ids = singleOrderIds;
    this.sharedOrder.next(sharedOrder);
    this.saveSharedOrdersId(sharedOrder);
    this.apiService.calculateOrder(locationId, order)
      .subscribe(({total_money, taxes, discounts, accumulated_loyalty_points, line_items, estimated_duration}: any) => {
        this.paymentStore.isWaitingUpdate = false;
        this.getAllCatalog(locationId, line_items);
        this.apiService.updateOrderData(locationId, order, orderMap, total_money, taxes, discounts, accumulated_loyalty_points, line_items, estimated_duration);
      }, () => {
        this.paymentStore.isWaitingUpdate = false;
      });
  }

  public clearSharedOrder(): void {
    sessionStorage.clear();
    this.sharedOrder.next([]);
    this.sharedOwnerId.next(null);
    this.sharedOrderId.next(null);
  }

  private getAllCatalog(locationId: string, line_items: any): void {
    const cartItems = [];
    line_items.map(item => {
      if (item.name.includes('Kyoo Service Charge')) {
        return;
      }
      cartItems.push(new MenuItemForPayment(
        locationId,
        item.catalog_object_id,
        item.uid,
        new MenuItemData(
          item.catalog_object_id,
          item.name,
          null,
          Number(item.quantity),
          new MenuItemVariation(
            item.variation_name,
            item.id,
            item.base_price_money
          ),
          item.modifiers?.map((modifier: any) =>
            new ProductModifier(
              modifier.catalog_object_id ?? '',
              modifier.name ?? '',
              new Price(
                modifier.base_price_money?.amount ?? 0,
                modifier.total_price_money?.currency ?? ''
              ),
              true,
              false,
              modifier?.hidden_from_customer
            )
          ),
        ),
        false,
        false,
        null,
        item.metadata
      ));
    });
    this.cartStore.setCartItems(cartItems);
  }

  public getStorageItems(): void {
    const savedItems = sessionStorage.getItem(UrlParam.cartItems);
    if (savedItems) {
      const items = JSON.parse(savedItems);
      this.cartStore.setCartItems(items);
    }
  }
  public getStorageSharedOrder(): void {
    const savedSharedOrders = sessionStorage.getItem(UrlParam.sharedOrderIds);
    if (savedSharedOrders) {
      this.sharedOrder.next(JSON.parse(savedSharedOrders));
    }
  }

  private saveSharedOrdersId(orderIds: any[]): void {
    sessionStorage.setItem(UrlParam.sharedOrderIds, JSON.stringify(orderIds));
  }

  public setStorageCartItems(): void {
    this.sharedOwnerIdSubscribe().subscribe(ownerId => {
      if (ownerId) {
        this.cartStore.items$.subscribe(cartItems => {
          const cartItemsString = JSON.stringify(cartItems);
          sessionStorage.setItem(UrlParam.cartItems, cartItemsString);
        });
      }
    });
  }
}
